import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import Flashcard from './components/Flashcard';
import AdminPage from './components/AdminPage';
import OwnerDashboard from './components/OwnerDashboard';
import UpgradePage from './components/UpgradePage';
import { useFlashcardSession } from './hooks/useFlashcardSession';
import { GET_WORDS, GET_TOPICS } from './graphql/queries';
import { playSessionCompleteSound, playNextSound, playPreviousSound } from './utils/soundEffects';
import './styles.css';

// Import modes
import ModeSelector from './modes/ModeSelector';
import ParagraphMode from './modes/paragraph/ParagraphMode';
import ParagraphAdmin from './modes/paragraph/ParagraphAdmin';
import QuizBattleMode from './modes/quiz-battle/QuizBattleMode';

// Auth
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import AuthCallback from './components/AuthCallback';
import StudentJoinPage from './components/StudentJoinPage';
import ClassJoinPage from './components/ClassJoinPage';
import ClassPanel from './components/ClassPanel';
import SetPasswordForm from './components/SetPasswordForm';
import Onboarding from './components/Onboarding';
import TeacherHome from './components/TeacherHome';
import PackBrowser from './components/PackBrowser';

/**
 * Parse the current URL path for the student join route.
 * Returns { isJoinRoute: boolean, roomCode: string | null }
 */
function parseJoinRoute() {
  const path = window.location.pathname;
  if (path === '/join' || path === '/join/') {
    return { isJoinRoute: true, roomCode: null };
  }
  const match = path.match(/^\/join\/([A-Za-z0-9]{1,10})$/);
  if (match) {
    return { isJoinRoute: true, roomCode: match[1].toUpperCase() };
  }
  return { isJoinRoute: false, roomCode: null };
}

// Class enrollment route — /class/:CODE.
function parseClassRoute() {
  const path = window.location.pathname;
  if (path === '/class' || path === '/class/') {
    return { isClassRoute: true, classCode: null };
  }
  const match = path.match(/^\/class\/([A-Za-z0-9]{4,10})$/);
  if (match) {
    return { isClassRoute: true, classCode: match[1].toUpperCase() };
  }
  return { isClassRoute: false, classCode: null };
}

function ProExpiryBanner({ teacher }) {
  if (!teacher || teacher.proSource !== 'promo' || !teacher.proExpiresAt) return null;

  const expiresAt = new Date(teacher.proExpiresAt);
  const now = new Date();
  const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

  if (daysLeft > 7 || daysLeft < 0) return null;

  return (
    <div style={{
      background: '#f5ebd1',
      borderBottom: '1px solid #e0cfa3',
      padding: '12px 32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: 14,
      fontFamily: 'var(--font-sans)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="badge badge--gold badge--mono">Pro trial</span>
        <span>
          Your trial expires in <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong>. Upgrade to keep audio recording, image uploads, and unlimited rooms.
        </span>
      </div>
    </div>
  );
}

/**
 * Main App Component
 *
 * Orchestrates the flashcard learning experience:
 * - Session management
 * - Progress tracking
 * - Session summary
 * - Navigation controls
 *
 * Flow:
 * 1. Welcome screen
 * 2. Active study session
 * 3. Session complete summary
 * 4. Option to restart or change word set
 *
 * PHASE 2 TODO:
 * - Add user authentication
 * - Fetch words from backend API based on user level
 * - Add word set selection (HSK levels, topics, custom decks)
 * - Add daily goal tracking
 * - Add streak counter
 * - Sync progress across devices
 * - Add settings panel (audio on/off, animation speed, etc.)
 *
 * GAME MODE TODO (Future Phase):
 * - Add "Start Game Mode" button after study session
 * - Transition to typing game with current word set
 * - Track typing speed and accuracy
 */
function AppContent() {
  const { teacher, logout, sessionType, studentSession, logoutStudent, refreshTeacher } = useAuth();
  const isStudent = sessionType === 'student';
  const [currentMode, setCurrentMode] = useState(null); // null = mode selector, 'classic' | 'arcade' | 'zen' | 'pop' | 'ink'
  const [hasStarted, setHasStarted] = useState(false);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [direction, setDirection] = useState('none');
  const [showAdminPage, setShowAdminPage] = useState(false);
  const [showParagraphAdmin, setShowParagraphAdmin] = useState(false);
  const [showOwnerDashboard, setShowOwnerDashboard] = useState(false);
  const [showUpgradePage, setShowUpgradePage] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPackBrowser, setShowPackBrowser] = useState(false);
  const [skipOnboarding, setSkipOnboarding] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);
  // Session duration tracking — set when the session starts, frozen when it
  // completes so we can show "in N minutes" without it ticking past.
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [sessionDurationMs, setSessionDurationMs] = useState(0);

  /**
   * Fetch available topics
   */
  const { data: topicsData } = useQuery(GET_TOPICS);
  const availableTopics = topicsData?.topics || [];

  /**
   * Fetch words from GraphQL backend with optional topic filter
   */
  const { data, previousData, loading, error, refetch } = useQuery(GET_WORDS, {
    variables: {
      topics: selectedTopics.length > 0 ? selectedTopics : undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  /**
   * All words from the backend. Falls back to the previous filter's data
   * while a new topic variant is in flight — without this, switching topics
   * briefly empties `data` (different Apollo cache key) and the loading
   * splash flashes.
   */
  const allWords = data?.words ?? previousData?.words ?? [];

  const {
    currentWord,
    currentIndex,
    totalWords,
    progress,
    sessionComplete,
    nextCard,
    previousCard,
    resetSession,
    hasNext,
    hasPrevious,
  } = useFlashcardSession(hasStarted ? allWords : [], shuffleMode);

  /**
   * Navigate to next card with animation
   */
  const handleNext = useCallback(() => {
    playNextSound();
    setDirection('next');
    nextCard();
  }, [nextCard]);

  /**
   * Navigate to previous card with animation
   */
  const handlePrevious = useCallback(() => {
    playPreviousSound();
    setDirection('previous');
    previousCard();
  }, [previousCard]);

  /**
   * Play celebration sound and freeze the duration when the session completes.
   */
  useEffect(() => {
    if (!sessionComplete) return;
    playSessionCompleteSound();
    if (sessionStartedAt) {
      setSessionDurationMs(Date.now() - sessionStartedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionComplete]);

  /**
   * Add arrow key navigation
   */
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft' && hasPrevious) {
        handlePrevious();
      } else if (e.key === 'ArrowRight' && hasNext) {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [hasNext, hasPrevious, handleNext, handlePrevious]);

  /**
   * Mode selector keyboard shortcuts (1-2)
   */
  useEffect(() => {
    const handleModeKeyPress = (e) => {
      if (currentMode !== null) return;
      const modes = ['classic', 'paragraph', 'quiz-battle'];
      const num = parseInt(e.key);
      if (num >= 1 && num <= 3) {
        setCurrentMode(modes[num - 1]);
      }
    };

    window.addEventListener('keydown', handleModeKeyPress);
    return () => window.removeEventListener('keydown', handleModeKeyPress);
  }, [currentMode]);

  /**
   * Start a new session. Resets the hook's internal state so that a fresh
   * deck is initialised — without this, the hook's `!sessionActive` gate
   * keeps the previous session's words even when the topic filter changes.
   */
  const handleStartSession = () => {
    resetSession();
    setSessionStartedAt(Date.now());
    setSessionDurationMs(0);
    setHasStarted(true);
  };

  // Returning to the mode selector should also reset Classic's session state,
  // otherwise re-entering Study skips the shuffle/sequential choice and drops
  // straight into the previous session.
  const handleBackToModes = useCallback(() => {
    setCurrentMode(null);
    setHasStarted(false);
    setDirection('none');
    setSessionStartedAt(null);
    setSessionDurationMs(0);
    resetSession();
  }, [resetSession]);

  /**
   * Handle session restart
   */
  const handleRestart = () => {
    resetSession();
    setHasStarted(false);
    setSessionStartedAt(Date.now());
    setSessionDurationMs(0);
    setTimeout(() => {
      setHasStarted(true);
    }, 100);
  };

  /**
   * Toggle topic selection
   */
  const toggleTopic = (topic) => {
    setSelectedTopics((prev) =>
      prev.includes(topic)
        ? prev.filter((t) => t !== topic)
        : [...prev, topic]
    );
  };

  /**
   * Clear all selected topics
   */
  const clearTopics = () => {
    setSelectedTopics([]);
  };

  /**
   * Refetch words after mutations
   */
  const handleRefreshWords = useCallback(() => {
    refetch();
  }, [refetch]);

  /**
   * Show loading state — Inkwell editorial
   */
  if (loading && allWords.length === 0) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--paper)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>Loading · 加载</div>
          <h1 className="t-display">Fetching flashcards…</h1>
        </div>
      </div>
    );
  }

  /**
   * Show error state — detect free plan limit and offer upgrade
   */
  if (error) {
    const isFreeLimit = error.message && error.message.toLowerCase().includes('free plan limit');
    if (isFreeLimit) return <UpgradePage onBack={() => refetch()} />;

    return (
      <div style={{
        minHeight: '100vh', background: 'var(--paper)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
      }}>
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <div className="t-eyebrow" style={{ marginBottom: 12, color: 'var(--vermillion)' }}>
            Connection error
          </div>
          <h1 className="t-display" style={{ marginBottom: 16 }}>
            Something went wrong.
          </h1>
          <p className="t-body" style={{ marginBottom: 24 }}>{error.message}</p>
          <button className="btn btn--lg" onClick={() => refetch()}>
            Retry connection
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render Owner Dashboard (admin only)
   */
  if (showOwnerDashboard) {
    return <OwnerDashboard onBack={() => setShowOwnerDashboard(false)} />;
  }

  /**
   * Render Paragraph Admin (manage reading passages)
   */
  if (showParagraphAdmin) {
    return <ParagraphAdmin onBack={() => setShowParagraphAdmin(false)} />;
  }

  /**
   * Render Word Admin (manage flashcards)
   */
  if (showAdminPage) {
    return (
      <AdminPage
        allWords={allWords}
        onRefresh={handleRefreshWords}
        onBack={() => setShowAdminPage(false)}
      />
    );
  }

  /**
   * First-run onboarding: only when the teacher has never completed onboarding
   * AND their library is empty. The flag is set server-side when they install a
   * starter pack OR explicitly skip. The empty-library check protects existing
   * users whose records pre-date the `hasOnboarded` field — they'll have
   * `hasOnboarded: false` in JSON (Go's zero value) but actual words, and should
   * not see the welcome screen.
   */
  if (
    teacher &&
    !isStudent &&
    !loading &&
    !teacher.hasOnboarded &&
    allWords.length === 0 &&
    !skipOnboarding &&
    currentMode === null &&
    !showSettings &&
    !showOwnerDashboard &&
    !showUpgradePage
  ) {
    return (
      <Onboarding
        teacher={teacher}
        onComplete={() => {
          handleRefreshWords();
          refreshTeacher();
          setSkipOnboarding(true);
        }}
        onSkip={() => {
          refreshTeacher();
          setSkipOnboarding(true);
        }}
      />
    );
  }

  /**
   * Render Upgrade Page
   */
  if (showUpgradePage) {
    return <UpgradePage onBack={() => setShowUpgradePage(false)} />;
  }

  /**
   * Render Pack Browser — teachers can install HSK packs after onboarding
   * (e.g. HSK 2–6 once they've upgraded to Pro).
   */
  if (showPackBrowser && teacher && !isStudent) {
    return (
      <PackBrowser
        teacher={teacher}
        onBack={() => setShowPackBrowser(false)}
        onInstalled={handleRefreshWords}
      />
    );
  }

  /**
   * Render Account Settings — Inkwell editorial layout
   */
  if (showSettings) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
        <header className="ink-header">
          <button className="btn btn--ghost btn--sm" onClick={() => setShowSettings(false)}>
            ← Back
          </button>
          <span style={{ fontWeight: 500 }}>Settings</span>
          <div style={{ width: 80 }} />
        </header>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '64px 32px' }}>
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>Account · 账户</div>
          <h1 className="t-h1" style={{ marginBottom: 40, fontSize: 32 }}>Settings</h1>

          {teacher && (
            <section className="card" style={{ padding: 28, marginBottom: 24 }}>
              <h2 className="t-h3" style={{ marginBottom: 16 }}>Account information</h2>
              <div style={{
                display: 'grid', gridTemplateColumns: '160px 1fr',
                rowGap: 12, fontSize: 14,
              }}>
                <span className="t-small">Email</span>
                <span>{teacher.email}</span>
                <span className="t-small">Plan</span>
                <span>
                  {teacher.plan === 'pro' ? (
                    <span className="badge badge--gold">Pro</span>
                  ) : (
                    <span className="badge">Free</span>
                  )}
                </span>
                {teacher.name && <><span className="t-small">Name</span><span>{teacher.name}</span></>}
              </div>
            </section>
          )}

          {teacher && <ClassPanel teacher={teacher} />}

          <section className="card" style={{ padding: 28 }}>
            <h2 className="t-h3" style={{ marginBottom: 4 }}>Set password</h2>
            <p className="t-small" style={{ marginBottom: 20 }}>
              Optional. Adds a fallback if email isn't available. Minimum 8 characters.
            </p>
            <SetPasswordForm />
          </section>
        </div>
      </div>
    );
  }

  /**
   * Mode Selection - Show mode selector or render selected mode
   */
  if (currentMode === null) {
    return (
      <>
        {isStudent && studentSession && (
          <div className="ink-topbar" style={{
            position: 'fixed',
            top: 16,
            right: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            zIndex: 100,
          }}>
            <span className="t-small" style={{ color: 'var(--ink-3)' }}>
              {studentSession.studentName} · {studentSession.teacherName}
            </span>
            <button className="btn btn--ghost btn--sm" onClick={logoutStudent}>
              Sign out
            </button>
          </div>
        )}
        {!isStudent && teacher && (
          <div className="ink-topbar" style={{
            position: 'fixed',
            top: 16,
            right: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            zIndex: 100,
          }}>
            <span className="t-small" style={{ color: 'var(--ink-3)' }}>
              {teacher.name || teacher.email}
            </span>
            {teacher.plan === 'pro' && teacher.proSource === 'promo' && teacher.proExpiresAt && (
              <span className="badge badge--gold">
                Pro · {Math.max(0, Math.ceil((new Date(teacher.proExpiresAt) - new Date()) / (1000 * 60 * 60 * 24)))}d left
              </span>
            )}
            {teacher.plan === 'pro' && teacher.proSource !== 'promo' && (
              <span className="badge badge--gold">Pro</span>
            )}
            <button className="btn btn--ghost btn--sm" onClick={() => setShowAdminPage(true)}>
              Words
            </button>
            <button className="btn btn--ghost btn--sm" onClick={() => setShowParagraphAdmin(true)}>
              Reading
            </button>
            {teacher.role === 'admin' && (
              <button className="btn btn--sm" onClick={() => setShowOwnerDashboard(true)}>
                Admin
              </button>
            )}
            {teacher.plan !== 'pro' && (
              <button className="btn btn--accent btn--sm" onClick={() => setShowUpgradePage(true)}>
                Upgrade
              </button>
            )}
            <button className="btn btn--ghost btn--sm" onClick={() => setShowSettings(true)}>
              Settings
            </button>
            <button className="btn btn--ghost btn--sm" onClick={logout}>
              Sign out
            </button>
          </div>
        )}
        {isStudent ? (
          <ModeSelector
            onSelectMode={(mode) => setCurrentMode(mode)}
            wordCount={allWords.length}
          />
        ) : (
          <TeacherHome
            teacher={teacher}
            wordCount={allWords.length}
            onSelectMode={(mode) => setCurrentMode(mode)}
            onOpenPacks={() => setShowPackBrowser(true)}
            onOpenWords={() => setShowAdminPage(true)}
            onOpenSettings={() => setShowSettings(true)}
          />
        )}
      </>
    );
  }

  // Render selected mode
  if (currentMode === 'paragraph') {
    return <ParagraphMode onBack={handleBackToModes} />;
  }

  if (currentMode === 'quiz-battle') {
    return <QuizBattleMode onBack={handleBackToModes} />;
  }

  // 'classic' mode falls through to the original app experience below

  /**
   * Render welcome screen — Inkwell editorial style
   */
  if (!hasStarted) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
        <header className="ink-header">
          <button className="btn btn--ghost btn--sm" onClick={handleBackToModes}>
            ← Modes
          </button>
          <span className="t-mono" style={{
            fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
          }}>
            Classic · 经典
          </span>
          <div style={{ width: 80 }} />
        </header>

        <div style={{ maxWidth: 720, margin: '0 auto', padding: '64px 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="t-eyebrow" style={{ marginBottom: 16 }}>
              Spaced repetition · 间隔复习
            </div>
            <h1 className="t-display" style={{ marginBottom: 12 }}>
              Master characters,<br />one stroke at a time.
            </h1>
            <p className="t-body" style={{ fontSize: 17 }}>
              {allWords.length} words ready · stroke animations · audio · examples
            </p>
          </div>

          {availableTopics.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16,
              }}>
                <h2 className="t-h3">Filter by topic</h2>
                {selectedTopics.length > 0 && (
                  <button
                    onClick={clearTopics}
                    style={{
                      background: 'transparent', border: 0, color: 'var(--ink-3)', fontSize: 13, cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                    }}
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {availableTopics.map((topic) => (
                  <button
                    key={topic}
                    className={`chip ${selectedTopics.includes(topic) ? 'chip--active' : ''}`}
                    onClick={() => toggleTopic(topic)}
                  >
                    {topic}
                  </button>
                ))}
              </div>
              <p className="t-small">
                Showing <strong style={{ color: 'var(--ink)' }}>{allWords.length} words</strong>
                {selectedTopics.length > 0 ? ` in: ${selectedTopics.join(', ')}` : ' across all topics'}
              </p>
            </section>
          )}

          <section style={{
            marginBottom: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: 'var(--paper-2)',
            border: '1px solid var(--rule)',
            borderRadius: 12,
          }}>
            <div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                {shuffleMode ? 'Shuffle mode' : 'Sequential mode'}
              </div>
              <div className="t-small">
                {shuffleMode ? 'Cards appear in random order each session.' : 'Words progress in order.'}
              </div>
            </div>
            <button
              onClick={() => setShuffleMode(!shuffleMode)}
              style={{
                width: 48, height: 26, borderRadius: 999,
                background: shuffleMode ? 'var(--ink)' : 'var(--rule)',
                border: 'none', position: 'relative', cursor: 'pointer',
                transition: 'background .15s',
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: shuffleMode ? 25 : 3,
                width: 20, height: 20, borderRadius: 999, background: '#fff',
                transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
              }} />
            </button>
          </section>

          <button
            className="btn btn--lg btn--block"
            onClick={handleStartSession}
            disabled={allWords.length === 0}
            style={{ marginBottom: 12 }}
          >
            {allWords.length > 0 ? `Start learning · ${allWords.length} words →` : 'No words available'}
          </button>

          {teacher && !isStudent && (
            <button
              className="btn btn--ghost btn--block"
              onClick={() => setShowAdminPage(true)}
            >
              Manage words
            </button>
          )}
        </div>
      </div>
    );
  }

  /**
   * Render session complete screen — Inkwell style.
   * Minimal hero only: how many words, how long. Stats cards and SRS
   * breakdown are intentionally absent — the UI only navigates with Next/Prev
   * (no rate buttons), so per-card ratings are always 0 and would mislead.
   */
  if (sessionComplete) {
    const durationMinutes = Math.max(0, Math.round(sessionDurationMs / 60000));
    const durationSeconds = Math.max(0, Math.round(sessionDurationMs / 1000));
    const durationLabel =
      sessionDurationMs <= 0 ? '' :
      durationMinutes === 0 ? `${durationSeconds} second${durationSeconds === 1 ? '' : 's'}` :
      `${durationMinutes} minute${durationMinutes === 1 ? '' : 's'}`;
    return (
      <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
        <header className="ink-header">
          <button className="btn btn--ghost btn--sm" onClick={handleBackToModes}>
            ← Modes
          </button>
          <span className="t-mono" style={{
            fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
          }}>
            Classic · Complete
          </span>
          <div style={{ width: 80 }} />
        </header>

        <div style={{ maxWidth: 560, margin: '0 auto', padding: '80px 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div className="t-eyebrow" style={{ marginBottom: 16 }}>
              Session complete · 完成
            </div>
            <h1 className="t-display" style={{ marginBottom: 12 }}>
              Great work today.
            </h1>
            <p className="t-body" style={{ fontSize: 17 }}>
              You reviewed {totalWords} word{totalWords === 1 ? '' : 's'}
              {durationLabel ? ` in ${durationLabel}` : ''}.
            </p>
          </div>

          <button className="btn btn--lg btn--block" onClick={handleRestart} style={{ marginBottom: 12 }}>
            Study again
          </button>
          {teacher && !isStudent && (
            <button className="btn btn--ghost btn--block" onClick={() => setShowAdminPage(true)}>
              Manage words
            </button>
          )}
        </div>
      </div>
    );
  }

  /**
   * Render active session — Inkwell editorial layout
   */
  return (
    <div style={{
      minHeight: '100vh', background: 'var(--paper)',
      display: 'flex', flexDirection: 'column',
    }}>
      <header className="ink-header">
        <button className="btn btn--ghost btn--sm" onClick={handleBackToModes}>
          ← Modes
        </button>
        <span className="t-mono" style={{
          fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
        }}>
          Classic · Session
        </span>
        <div style={{ width: 80 }} />
      </header>

      {/* Progress */}
      <div style={{ padding: '24px clamp(16px, 6vw, 80px) 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span className="t-mono" style={{ fontSize: 12, letterSpacing: '0.08em' }}>
            {currentIndex + 1} / {totalWords}
          </span>
          <span className="t-mono" style={{ fontSize: 12, letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
            {progress}%
          </span>
        </div>
        <div className="progress-track" style={{ height: 2 }}>
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '32px clamp(16px, 6vw, 80px)',
      }}>
        <Flashcard word={currentWord} direction={direction} />
      </div>

      <div style={{
        height: 80, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 clamp(16px, 6vw, 80px)', borderTop: '1px solid var(--rule)',
      }}>
        <button className="btn btn--ghost" onClick={handlePrevious} disabled={!hasPrevious}>
          ← Previous
        </button>
        <span className="t-small">{currentIndex + 1} of {totalWords}</span>
        <button className="btn" onClick={handleNext} disabled={!hasNext}>
          {currentIndex === totalWords - 1 ? 'Finish →' : 'Next →'}
        </button>
      </div>
    </div>
  );
}

function AppRouter() {
  const { teacher, studentSession, sessionType, loading } = useAuth();

  // Public student join route — no auth required
  const { isJoinRoute, roomCode } = parseJoinRoute();
  if (isJoinRoute) {
    return <StudentJoinPage initialRoomCode={roomCode} />;
  }

  // Class enrollment route — drop students into the join form unless they
  // already have an active class session for this code.
  const { isClassRoute, classCode } = parseClassRoute();
  if (isClassRoute) {
    if (sessionType === 'student' && studentSession?.classCode === classCode) {
      // Already joined — fall through to AppContent below.
    } else {
      return <ClassJoinPage initialClassCode={classCode} />;
    }
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--paper)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div
            className="stamp"
            style={{
              width: 56, height: 56, fontSize: 28,
              background: 'var(--vermillion)', color: '#fff',
              margin: '0 auto 16px',
            }}
          >
            加
          </div>
          <div className="t-eyebrow">Loading</div>
        </div>
      </div>
    );
  }

  // Handle magic link callback
  if (window.location.pathname === '/auth/verify') {
    return <AuthCallback />;
  }

  // Active student session — render AppContent in student mode.
  if (sessionType === 'student' && studentSession) {
    return <AppContent />;
  }

  // Require login
  if (!teacher) return <LoginPage />;

  return (
    <>
      <ProExpiryBanner teacher={teacher} />
      <AppContent />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

export default App;
